<template>
  <!-- Kasutajad in the hooldusplaat look (Seaded -> Kasutajad uues
       kujunduses): everyone on one page, each in the colour the Kalender
       draws them in, with their roles, when they last logged in, their recent
       and next töökäsk, and what is set up for them (planeerija, allkiri,
       Google). A lock is said on the row, with the way to open it. -->
  <div class="ul">
    <div class="ul-head">
      <div>
        <div class="ul-cap">Haldus</div>
        <h1 class="ul-title">Kasutajad</h1>
      </div>
      <div class="d-flex flex-wrap align-center ga-3">
        <div class="ul-seg" role="group" aria-label="Olek">
          <button
            v-for="option in stateOptions"
            :key="option.value"
            type="button"
            :aria-pressed="String(state === option.value)"
            @click="state = option.value"
          >{{ option.text }}<small>{{ counts[option.value] }}</small></button>
        </div>
        <router-link :to="{ name: 'user/create' }" class="ul-btn">+ Lisa kasutaja</router-link>
      </div>
    </div>

    <div class="ul-filters">
      <label class="ul-search">
        <v-icon size="20">mdi-magnify</v-icon>
        <input v-model="q" type="search" placeholder="Nimi, e-post või telefon" aria-label="Otsi">
      </label>
      <div class="ul-chips" role="group" aria-label="Roll">
        <button
          v-for="option in roleOptions"
          :key="option.value"
          type="button"
          class="ul-chip"
          :aria-pressed="String(role === option.value)"
          @click="role = role === option.value ? '' : option.value"
        >{{ option.text }} <small>{{ roleCount(option.value) }}</small></button>
        <button
          v-if="lockedCount"
          type="button"
          class="ul-chip ul-chip--bad"
          :aria-pressed="String(lockedOnly)"
          @click="lockedOnly = !lockedOnly"
        >Lukus <small>{{ lockedCount }}</small></button>
        <!-- The Töölaud card's filter, which has no control of its own here. -->
        <button v-if="lastLogin" type="button" class="ul-chip ul-chip--amber" aria-pressed="true" @click="lastLogin = null">
          Pole loginud üle {{ lastLogin }} päeva ✕
        </button>
      </div>
    </div>

    <v-progress-linear v-if="loading && !users.length" indeterminate color="green" height="3" />
    <div v-else class="ul-card">
      <div v-if="!rows.length" class="ul-empty">Sellist kasutajat pole.</div>
      <div v-for="user in rows" :key="user.id" class="ul-row" :class="{ 'ul-row--off': !user.active }">
        <span class="ul-ini" :style="dot(user.color)">{{ initials(user.fullName) }}</span>
        <div class="ul-who">
          <router-link :to="{ name: 'user', params: { userId: user.id } }" class="ul-name">{{ user.fullName }}</router-link>
          <span class="ul-sub">{{ user.email }}<template v-if="phones(user)"> · {{ phones(user) }}</template></span>
          <span class="ul-tags">
            <span v-for="r in sortedRoles(user)" :key="r" class="ul-tag" :class="'ul-tag--' + r">{{ ROLE_TEXT[r] || r }}</span>
            <span v-if="!user.active" class="ul-tag ul-tag--off">Mitteaktiivne</span>
            <span v-if="user.isLocked" class="ul-tag ul-tag--bad">{{ user.locked ? 'Lukus (püsiv)' : 'Lukus' }}</span>
          </span>
        </div>
        <div class="ul-col">
          <span class="ul-k">VIIMATI SEES</span>
          <span :class="{ 'ul-amber': staleLogin(user) }">{{ loginText(user) }}</span>
        </div>
        <div class="ul-col">
          <span class="ul-k">TÖÖKÄSUD</span>
          <span v-if="user.work && (user.work.recent || user.work.next)">
            {{ user.work.recent }} viimase 30 päevaga<template v-if="user.work.next"><br><span class="ul-sub">järgmine {{ when(user.work.next) }}</span></template>
          </span>
          <span v-else class="ul-none">—</span>
        </div>
        <div class="ul-set" aria-label="Seadistatud">
          <span class="ul-flag" :class="{ on: user.showInPlanner }" :title="user.showInPlanner ? 'Kuvatakse planeerijas' : 'Planeerijas ei kuvata'">Planeerija</span>
          <span class="ul-flag" :class="{ on: user.hasSignature }" :title="user.hasSignature ? 'Allkiri salvestatud' : 'Allkirja pole'">Allkiri</span>
          <span class="ul-flag" :class="{ on: user.hasGoogleToken }" :title="user.hasGoogleToken ? 'Google Kalender ühendatud' : 'Google Kalender ühendamata'">Google</span>
        </div>
        <div class="ul-acts">
          <button v-if="user.isLocked" type="button" class="ul-btn ul-btn--sm" :disabled="busy" @click="unlock(user)">Ava lukk</button>
          <router-link :to="{ name: 'user', params: { userId: user.id } }" class="ul-btn ul-btn--sm ul-btn--outline">Muuda</router-link>
          <v-menu location="bottom end">
            <template #activator="{ props }">
              <button type="button" class="ul-icon" aria-label="Rohkem" v-bind="props"><v-icon>mdi-dots-vertical</v-icon></button>
            </template>
            <div class="ul-menu">
              <button type="button" class="ul-menu__danger" @click="confirmDelete = user">Kustuta kasutaja</button>
            </div>
          </v-menu>
        </div>
      </div>
      <div v-if="rows.length" class="ul-foot">{{ rows.length }} / {{ users.length }}</div>
    </div>

    <confirm
      :visible="!!confirmDelete"
      title="Kustuta kasutaja?"
      heading="Kustuta kasutaja?"
      text="Ta ei saa enam sisse logida. Tema töökäsud ja märkused jäävad alles."
      action="Kustuta kasutaja"
      :detail="confirmDelete ? confirmDelete.fullName : ''"
      :detail-sub="confirmDelete ? confirmDelete.email : ''"
      @cancel="confirmDelete = null"
      @confirm="destroy()"
    >
      <template v-if="confirmDelete && !features.dialogPlate">{{ confirmDelete.fullName }} ei saa enam sisse logida. Tema töökäsud ja märkused jäävad alles.</template>
    </confirm>
  </div>
</template>

<script>
import api from '@/api'
import dayjs from 'dayjs'
import { mapActions, mapState } from 'vuex'
import loadArchivo from '@/plugins/archivo'
import { initials } from '@/components/frame/frameLinks'

// In the order a row lists them, in the words the office uses (frameLinks.roleLabel).
const ROLE_ORDER = ['root', 'admin', 'secretary', 'tehnic']
const ROLE_TEXT = { root: 'Süsteemi haldur', admin: 'Administraator', secretary: 'Sekretär', tehnic: 'Tehnik' }
// Longer than this since the last login, an active user's date is amber.
const STALE_DAYS = 30

export default {
  data () {
    return {
      ROLE_TEXT,
      loading: false,
      busy: false,
      users: [],
      q: '',
      role: '',
      lockedOnly: false,
      // The Töölaud's "Tehnikute sisselogimised" card sends ?active=1&lastLogin=N.
      state: this.$route.query.active === '0' ? 'off' : 'on',
      lastLogin: parseInt(this.$route.query.lastLogin) || null,
      confirmDelete: null,
      stateOptions: [
        { value: 'on', text: 'Aktiivsed' },
        { value: 'off', text: 'Mitteaktiivsed' },
        { value: 'all', text: 'Kõik' }
      ]
    }
  },
  computed: {
    ...mapState(['features']),
    counts () {
      return {
        on: this.users.filter(user => user.active).length,
        off: this.users.filter(user => !user.active).length,
        all: this.users.length
      }
    },
    inState () {
      return this.users.filter(user => this.state === 'all' || (this.state === 'on') === !!user.active)
    },
    roleOptions () {
      return ['tehnic', 'secretary', 'admin', 'root'].map(value => ({ value, text: ROLE_TEXT[value] }))
    },
    lockedCount () {
      return this.inState.filter(user => user.isLocked).length
    },
    rows () {
      const q = this.q.trim().toLowerCase()
      const digits = q.replace(/\D/g, '')
      return this.inState.filter(user => {
        if (this.role && !this.titles(user).includes(this.role)) return false
        if (this.lockedOnly && !user.isLocked) return false
        if (this.lastLogin && user.lastLogin && dayjs().diff(dayjs(user.lastLogin), 'day') <= this.lastLogin) return false
        if (!q) return true
        const text = [user.fullName, user.email].join(' ').toLowerCase()
        const phone = [user.phone1, user.phone2].join(' ').replace(/\D/g, '')
        return text.includes(q) || (digits.length >= 3 && phone.includes(digits))
      }).sort((a, b) => (a.fullName || '').localeCompare(b.fullName || '', 'et'))
    }
  },
  watch: {
    // Back to a plain address once the card's filter is cleared.
    lastLogin (value) {
      if (!value && this.$route.query.lastLogin) {
        this.$router.replace({ query: {} }).catch(() => {})
      }
    }
  },
  created () {
    loadArchivo()
    this.refresh()
  },
  methods: {
    ...mapActions(['showNotification']),
    initials,
    async refresh () {
      this.loading = true
      try {
        const { users } = await api.getUsers({ limit: 500, stats: true })
        this.users = users
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.loading = false
      }
    },
    titles (user) {
      return (user.roles || []).map(role => role.title)
    },
    sortedRoles (user) {
      const titles = this.titles(user)
      return ROLE_ORDER.filter(title => titles.includes(title)).concat(titles.filter(title => !ROLE_ORDER.includes(title)))
    },
    roleCount (title) {
      return this.inState.filter(user => this.titles(user).includes(title)).length
    },
    phones (user) {
      return [user.phone1, user.phone2].filter(Boolean).join(', ')
    },
    staleLogin (user) {
      return !!user.active && (!user.lastLogin || dayjs().diff(dayjs(user.lastLogin), 'day') > STALE_DAYS)
    },
    loginText (user) {
      if (!user.lastLogin) return 'pole loginud'
      const days = dayjs().startOf('day').diff(dayjs(user.lastLogin).startOf('day'), 'day')
      if (days === 0) return 'täna ' + dayjs(user.lastLogin).format('HH:mm')
      if (days === 1) return 'eile ' + dayjs(user.lastLogin).format('HH:mm')
      if (days < 7) return days + ' päeva tagasi'
      return dayjs(user.lastLogin).format('DD.MM.YYYY')
    },
    when (date) {
      const d = dayjs(date)
      return d.isSame(dayjs(), 'day') ? 'täna ' + d.format('HH:mm') : d.format('DD.MM HH:mm')
    },
    // A person's colour behind the initials; a pale one gets dark ink.
    dot (color) {
      if (!color || !/^#?[0-9a-f]{3,8}$/i.test(color)) return { background: '#5B6B66', color: '#fff' }
      const hex = color.replace('#', '')
      const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex.slice(0, 6)
      const [r, g, b] = [0, 2, 4].map(i => parseInt(full.slice(i, i + 2), 16))
      const light = (0.299 * r + 0.587 * g + 0.114 * b) > 150
      return { background: '#' + full, color: light ? '#16322A' : '#fff' }
    },
    async unlock (user) {
      this.busy = true
      try {
        await api.unlockUser(user.id)
        this.showNotification({ text: 'Konto lukk eemaldatud', color: 'success' })
        await this.refresh()
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.busy = false
      }
    },
    async destroy () {
      const user = this.confirmDelete
      this.confirmDelete = null
      if (!user) return
      this.busy = true
      try {
        await api.deleteUser(user.id)
        this.showNotification({ text: 'Kasutaja kustutatud', color: 'success' })
        await this.refresh()
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.busy = false
      }
    }
  }
}
</script>

<style scoped>
.ul {
  --u-surface: #FFFFFF; --u-ink: #16322A; --u-ink-2: #3E504A; --u-ink-3: #5B6B66;
  --u-line: #C9D2CE; --u-line-soft: #E1E7E4; --u-green: #2E7D32; --u-green-dark: #1B5E20; --u-green-bg: #EAF3EA;
  --u-blue: #1F5FD1; --u-blue-bg: #EEF3FC; --u-amber: #8F5400; --u-amber-bg: #FFF4E0; --u-red: #B3261E; --u-red-bg: #FCEDEC; --u-grey-bg: #E9EEEC;
  font-family: 'Archivo Variable', 'Archivo', 'Helvetica Neue', Helvetica, Arial, sans-serif; color: var(--u-ink); max-width: 1280px; padding-bottom: 40px;
}
.ul button, .ul input { font: inherit; color: inherit; }
.ul :focus-visible { outline: 3px solid var(--u-blue); outline-offset: 2px; }
.ul-head { display: flex; flex-wrap: wrap; align-items: end; justify-content: space-between; gap: 12px; margin-bottom: 14px; }
.ul-cap { font-size: 12px; letter-spacing: .08em; text-transform: uppercase; color: var(--u-ink-3); font-weight: 600; }
.ul-title { font-size: 30px; font-weight: 600; margin: 2px 0 0; letter-spacing: -.01em; line-height: 1.2; }
.ul-seg { display: inline-flex; border: 1px solid var(--u-line); border-radius: 8px; overflow: hidden; background: var(--u-surface); }
.ul-seg button { border: 0; background: transparent; padding: 7px 14px; cursor: pointer; font-weight: 500; color: var(--u-ink-2); font-variant-numeric: tabular-nums; }
.ul-seg button + button { border-left: 1px solid var(--u-line); }
.ul-seg button[aria-pressed="true"] { background: var(--u-ink); color: #fff; }
.ul-seg small { opacity: .7; margin-left: 5px; }
.ul .ul-btn {
  height: 38px; padding: 0 16px; border-radius: 6px; border: 0; background: var(--u-green); color: #fff; cursor: pointer;
  font-weight: 700; font-size: 14px; display: inline-flex; align-items: center; text-decoration: none; white-space: nowrap;
}
.ul .ul-btn--sm { height: 32px; padding: 0 12px; font-size: 13px; }
.ul .ul-btn--outline { background: var(--u-surface); color: var(--u-green); border: 1.5px solid var(--u-green); font-weight: 600; }
.ul .ul-btn:disabled { opacity: .6; cursor: default; }
.ul-icon { width: 32px; height: 32px; border-radius: 6px; border: 1.5px solid var(--u-line); background: var(--u-surface); display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
.ul-menu {
  min-width: 180px; background: #fff; border: 1px solid #9AAAA4; border-radius: 8px; box-shadow: 0 12px 32px rgba(22, 50, 42, .2); padding: 6px;
  font-family: 'Archivo Variable', 'Archivo', 'Helvetica Neue', Helvetica, Arial, sans-serif;
}
.ul-menu button { display: flex; align-items: center; width: 100%; height: 36px; padding: 0 10px; border: 0; background: none; border-radius: 5px; font: inherit; font-size: 14px; cursor: pointer; }
.ul-menu button:hover { background: #F4F6F5; }
.ul-menu .ul-menu__danger { color: #B3261E; }

.ul-filters { display: flex; flex-wrap: wrap; gap: 10px 14px; align-items: center; margin-bottom: 12px; }
.ul-search { flex: 1 1 280px; min-width: 0; display: flex; align-items: center; gap: 8px; border: 1px solid var(--u-line); border-radius: 8px; padding: 0 12px; background: var(--u-surface); color: var(--u-ink-3); }
.ul-search input { border: 0; outline: 0; background: transparent; padding: 9px 0; width: 100%; min-width: 0; color: var(--u-ink); }
.ul-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.ul-chip { border: 1px solid var(--u-line); background: var(--u-surface); border-radius: 999px; padding: 5px 12px; cursor: pointer; font-size: 13px; }
.ul-chip small { color: var(--u-ink-3); font-weight: 700; }
.ul-chip[aria-pressed="true"] { background: var(--u-green-bg); border-color: var(--u-green); color: var(--u-green-dark); }
.ul-chip--bad[aria-pressed="true"] { background: var(--u-red-bg); border-color: var(--u-red); color: var(--u-red); }
.ul-chip--amber[aria-pressed="true"] { background: var(--u-amber-bg); border-color: var(--u-amber); color: var(--u-amber); }

.ul-card { background: var(--u-surface); border: 1px solid var(--u-line); border-radius: 12px; overflow: hidden; }
.ul-row { display: grid; grid-template-columns: 40px minmax(0, 1.6fr) minmax(0, .8fr) minmax(0, 1fr) auto auto; gap: 14px; align-items: center; padding: 12px 16px; border-top: 1px solid var(--u-line-soft); }
.ul-row:first-child { border-top: 0; }
.ul-row--off .ul-ini { opacity: .45; }
.ul-row--off .ul-name { color: var(--u-ink-3); }
.ul-ini { width: 36px; height: 36px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; }
.ul-who { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.ul .ul-name { font-size: 15.5px; font-weight: 700; color: var(--u-ink); text-decoration: none; overflow-wrap: anywhere; }
.ul .ul-name:hover { text-decoration: underline; }
.ul-sub { font-size: 12.5px; color: var(--u-ink-3); overflow-wrap: anywhere; }
.ul-tags { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 2px; }
.ul-tag { font-size: 11px; font-weight: 700; letter-spacing: .03em; padding: 0 7px; line-height: 19px; border-radius: 10px; background: var(--u-grey-bg); color: var(--u-ink-2); }
.ul-tag--tehnic { background: var(--u-green-bg); color: var(--u-green-dark); }
.ul-tag--secretary { background: var(--u-blue-bg); color: #2B3F66; }
.ul-tag--admin, .ul-tag--root { background: var(--u-amber-bg); color: #5B3700; }
.ul-tag--off { background: var(--u-surface); box-shadow: inset 0 0 0 1px var(--u-line); color: var(--u-ink-3); }
.ul-tag--bad { background: var(--u-red-bg); color: var(--u-red); box-shadow: inset 0 0 0 1px var(--u-red); }
.ul-col { display: flex; flex-direction: column; gap: 2px; font-size: 13.5px; min-width: 0; }
.ul-k { font-size: 10.5px; font-weight: 700; letter-spacing: .07em; color: var(--u-ink-3); }
.ul-amber { color: var(--u-amber); font-weight: 600; }
.ul-none { color: var(--u-ink-3); }
.ul-set { display: flex; gap: 4px; flex-wrap: wrap; justify-content: flex-end; }
.ul-flag { font-size: 11px; font-weight: 600; padding: 0 7px; line-height: 20px; border-radius: 10px; color: var(--u-ink-3); box-shadow: inset 0 0 0 1px var(--u-line-soft); text-decoration: line-through; text-decoration-color: rgba(91, 107, 102, .5); }
.ul-flag.on { color: var(--u-green-dark); background: var(--u-green-bg); box-shadow: none; text-decoration: none; }
.ul-acts { display: flex; gap: 6px; align-items: center; justify-content: flex-end; }
.ul-empty { padding: 18px; color: var(--u-ink-3); font-size: 14px; }
.ul-foot { padding: 10px 16px; border-top: 1px solid var(--u-line-soft); font-size: 12.5px; color: var(--u-ink-3); background: #FAFBFA; font-variant-numeric: tabular-nums; }

@media (max-width: 1100px) {
  .ul-row { grid-template-columns: 40px minmax(0, 1fr) minmax(0, 1fr); }
  .ul-set { grid-column: 2 / -1; justify-content: flex-start; }
  .ul-acts { grid-column: 2 / -1; justify-content: flex-start; }
}
@media (max-width: 600px) {
  .ul-title { font-size: 24px; }
  .ul-row { grid-template-columns: 40px minmax(0, 1fr); }
  .ul-col { grid-column: 2; }
}
</style>
